// @flow

import * as R from "ramda";

import { filterByOverdue } from "src/utils/filters";
import { getNotificationApprovalInfo } from "src/utils/checklist";
import * as notifications from "src/constants/notifications";

import type {
  UnifizeChatRoom,
  RoomId,
  TemplateStatus,
  Workflow,
  UnifizeChat,
  ChecklistFieldNotification
} from "src/types";

/**
 * Check whether the given argument is a valid Unifize chatroom.
 * @param {object | null} chatroom - The chatroom to check.
 */
export const isValidChatroom = (chatroom: ?Object = {}) =>
  // $FlowFixMe
  R.all(R.has(R.__, chatroom))([
    "status",
    "id",
    "address",
    "title",
    "type",
    "seqNo"
  ]);

// $FlowFixMe
export const sortByAlphabetical = R.sort<UnifizeChatRoom>((roomA, roomB) => {
  const titleA = (roomA.title || "").toLowerCase();
  const titleB = (roomB.title || "").toLowerCase();

  if (titleA < titleB) return -1;
  if (titleA > titleB) return 1;

  return Number(roomA.id) - Number(roomB.id);
});

// $FlowFixMe
export const sortByAutoNumber = R.sort<UnifizeChatRoom>((roomA, roomB) => {
  if (!roomA.seqNo || !roomB.seqNo) return Number(roomA.id) - Number(roomB.id);

  // If autoNo is not present or if the chatroom has multiple versions
  // (contains "/") rely on the seqNo for sorting instead of using the
  // autoNo
  if (
    !roomA.autoNo ||
    !roomB.autoNo ||
    (roomA.autoNo && !roomA.autoNo.includes("/")) ||
    (roomB.autoNo && !roomB.autoNo.includes("/"))
  )
    return Number(roomA.seqNo) - Number(roomB.seqNo);

  let [seqA, versionA] = roomA.autoNo.split("/").map(Number);
  let [seqB, versionB] = roomB.autoNo.split("/").map(Number);

  if (seqA !== seqB) return seqA - seqB;

  return versionA - versionB;
});

// $FlowFixMe
export const reverseSortById = R.sort<UnifizeChatRoom>(
  (a, b) => parseInt(b.id, 10) - parseInt(a.id, 10)
);

/**
 * Returns a function that checks if a chatroom title matches a given text,
 * including template title and sequence number.
 * @param {string} text - The text to search for within the chatroom's title.
 * @param {Workflow} workflows - The map of workflow templates used to
 * extract titles for ChatRooms.
 * @returns {function(UnifizeChatRoom): boolean} - Returns a predicate function to
 * evaluate if the chatroom title matches the provided text.
 */
export const containsTitle =
  (text: string, workflows: Workflow) => (chat: UnifizeChatRoom) => {
    if (chat) {
      const processTitle = chat.templateId
        ? workflows[`${chat.templateId}`] || ""
        : "";

      const seqNo = chat.seqNo || "";
      const title = chat.title || "";

      return (
        R.includes(
          R.toLower(text),
          `${R.toLower(processTitle)} ${seqNo}:${R.toLower(title)}`
        ) ||
        R.includes(
          R.toLower(text),
          `${R.toLower(processTitle)} #${seqNo}:${R.toLower(title)}`
        ) ||
        R.includes(
          R.toLower(text),
          `${R.toLower(processTitle)}#${seqNo}:${R.toLower(title)}`
        )
      );
    }
    return false;
  };

/**
 * Returns if a chatroom is cancelled or not.
 * @param {UnifizeChatRoom} chat - ChatRoom
 * @returns {boolean} - returns boolean.
 */
export const notCanceled = (chat: UnifizeChatRoom) => {
  if (chat) {
    return !chat.canceled;
  }
  return false;
};

/**
 * Returns a function that checks if a chatroom matches the specific workflow.
 * @param {string} workflow - Workflow ID
 * @returns {function(UnifizeChatRoom): boolean} - returns predicate function.
 */
export const containsWorkflow =
  (workflow?: string) => (chat: UnifizeChatRoom) => {
    if (workflow) {
      return (
        parseInt(workflow, 10) === chat.templateId && chat.type === "workflow"
      );
    }
    return true;
  };

/**
 * Returns a function that checks if a chatroom matches the specific type.
 * @param {string} type - The type of chatroom to check against.
 *  * @returns {function(ChatRoom): boolean} - Returns a predicate function
 * to evaluate if the chatroom matches the type.
 */
export const containsType = (type: string) => (chat: UnifizeChatRoom) => {
  return (
    (chat.type !== "direct" && type === chat.type && type !== undefined) ||
    (chat.type !== "direct" && type === undefined)
  );
};

/**
 * Returns a function that a chatroom matches the overdue condition.
 * @param {boolean} overdue - Indicates whether to filter for overdue ChatRooms.
 * @returns {function(UnifizeChatRoom): boolean} - Returns a predicate function
 * to evaluate if the chatroom meets the overdue condition.
 */
export const containsOverdue =
  (overdue: boolean) => (chat: UnifizeChatRoom) => {
    if (overdue)
      return chat
        ? filterByOverdue({ overdue })(chat) &&
            chat.active !== false &&
            !chat.canceled
        : false;
    return true;
  };

/**
 * Returns a function that checks if a chatroom matches the completed condition.
 * @param {boolean} completed - Indicates whether to filter for completed ChatRooms.
 * @returns {function(UnifizeChatRoom): boolean} - Returns a predicate function to
 * evaluate if the chatroom meets the completed condition.
 */
export const containsCompleted =
  (completed: boolean): Function =>
  (chat: UnifizeChatRoom): boolean => {
    if (completed) return chat && (chat.active || chat.canceled || false);
    return true;
  };

/**
 * Returns a function that checks if a chatroom matches the pending condition.
 * @param {boolean} pending - Indicates whether to filter for pending ChatRooms.
 * @returns {function(UnifizeChatRoom): boolean} - Returns a predicate function to
 * evaluate if the chatroom meets the pending condition.
 */
export const containsPending =
  (pending: boolean) => (chat: UnifizeChatRoom) => {
    if (pending) return chat ? chat.active !== false && !chat.canceled : false;
    return true;
  };

/**
 * Returns a function that checks if a chatroom is owned by a specific user.
 * @param {boolean} owner - Indicates whether to filter for ownership.
 * @param {string} uid - The user ID to check against the chatroom owner.
 * @returns {function(UnifizeChatRoom): boolean} - Returns a predicate function
 * to evaluate if the chatroom is owned by the specified user.
 */
export const containsOwner =
  (owner: boolean, uid: string) => (chat: UnifizeChatRoom) => {
    if (owner) return chat ? chat.owner === uid : false;
    return true;
  };

/**
 * Returns a function that checks if a chatroom is archived or should be filtered
 * out based on archived status.
 * @param {?boolean} shouldFilterOutArchived - Indicates whether to filter out
 * archived ChatRooms.
 * @param {TemplateStatus} templateStatus - The status of templates, used to
 * determine auto-archive status.
 * @returns {function(UnifizeChatRoom): boolean} - Returns a predicate function to
 * evaluate if the chatroom should be filtered based on its archived status.
 */
export const containsArchived =
  (shouldFilterOutArchived: ?boolean, templateStatus: TemplateStatus) =>
  (chat: UnifizeChatRoom) => {
    if (shouldFilterOutArchived) {
      // If chatroom belongs to a process, get auto archive
      const chatroomAutoArchived = chat.templateId
        ? templateStatus[`${chat.templateId}`]?.[`${chat.status}`]
            ?.archiveMode === "archive"
        : false;
      const manuallyArchived = chat.archived;
      const archived = R.isNil(manuallyArchived)
        ? chatroomAutoArchived
        : manuallyArchived;

      return chat ? !archived : false;
    }

    return true;
  };

/**
 * Checks whether the filter that the user is trying to delete is a
 * custom filter and is currently applied on the conversation list
 * @param {object} currentFilter - details of the currently applied
 * filter on conversation list
 * @param {string} filterToBeDeleted - name of the filter that the user
 * is trying to delete
 * @returns {boolean} - whether or not the current filter should be
 * deleted
 */
export const isSavedFilterApplied = (
  currentFilter: Object,
  filterToBeDeleted: string
) => {
  return (
    currentFilter.name === filterToBeDeleted && currentFilter.isSavedFilter
  );
};

/**
 * Retrieves the author of a checklist notification.
 *
 * @param {UnifizeChat} chatMessage - The chat message object.
 * @returns {string | null} The author of the checklist notification, or
 * null if the chat message is not a checklist notification.
 */
export const getChecklistNotificationAuthor = (chatMessage: UnifizeChat) => {
  if (chatMessage.action === notifications.CHECKLIST_FIELD_FILL) {
    return chatMessage.author;
  }
  return null;
};

/**
 * Determines if a chat message can be collapsed in the UI.
 *
 * Messages are collapsible if they are notifications, except for:
 * - Conversation start notifications
 * - Email sent/received notifications
 * - Checklist field notifications containing approved or rejected
 * approvals (not requests)
 * - Checklist fields that have been excluded from collapse
 *
 * @param {UnifizeChat} message - The chat message to check
 * @param {FieldsById} fieldsById - The fieldsById slice from checklist
 * state to check the excludeFromCollapse property.
 * @returns {boolean} True if the message can be collapsed, false otherwise
 */
export const isNotificationCollapsible = (
  message: UnifizeChat | null,
  index?: number = -1,
  excludedFields?: Set<string>
) => {
  if (!message || message.type !== "NOTIFICATION") return false;

  switch (message.action) {
    case notifications.CONVERSATION_START:
      return message.roomId !== String(message.data?.id) && index !== 0;
    case notifications.EMAIL_SENT:
    case notifications.EMAIL_RECEIVED:
    case notifications.FILE_ADD:
      return false;
    case notifications.AI_FIELD_FILL_SUGGESTIONS:
      return message.data != null && message.data?.state === "FILLED";
    case notifications.CHECKLIST_FIELD_FILL:
      try {
        if (message.data && message.data.fields) {
          const fieldIds = Object.keys(message.data.fields);
          if (
            fieldIds.some(fieldId => {
              const field = message.data?.fields[fieldId];
              const approvalInfo = getNotificationApprovalInfo(field);
              return (
                (approvalInfo.approval && approvalInfo.request === null) ||
                // $FlowFixMe - Optional chaining
                excludedFields?.has(fieldId)
              );
            })
          ) {
            return false;
          }
        }
      } catch (err) {
        console.error(err);
      }
      return true;
    default:
      return true;
  }
};

/**
 * Groups checklist notification fields into ordered segments of forms and
 * fields.
 *
 * Iterates through the provided field IDs, grouping them into segments based on
 * their type:
 * - Fields containing forms are grouped individually as type "form".
 * - Consecutive fields without forms are grouped together as type "fields".
 *
 * @param {Array<any>} fieldIds - An ordered array of field IDs to group.
 * @param {{ [string]: ChecklistFieldNotification }} fields - Fields from the
 * checklist notification data payload.
 * @returns {Array<{ type: "form" | "fields", ids: Array<string> }>} An array of
 * grouped field segments, preserving the original order.
 */
export const groupChecklistNotificationFields = (
  fieldIds: Array<any>,
  fields: { [string]: ChecklistFieldNotification }
) => {
  const groupedFields: Array<{
    type: "form" | "fields",
    ids: Array<string>
  }> = [];
  let remainingFields: Array<string> = [];
  fieldIds.forEach(id => {
    const field = fields[id];
    if (R.has("forms")(field)) {
      if (remainingFields.length > 0) {
        groupedFields.push({ type: "fields", ids: remainingFields });
        remainingFields = [];
      }
      groupedFields.push({ type: "form", ids: [id] });
    } else {
      remainingFields.push(id);
    }
  });
  if (remainingFields.length > 0) {
    groupedFields.push({ type: "fields", ids: remainingFields });
  }
  return groupedFields;
};
